import React, { FC, useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import AppInput from "@/components/forms/Fields/inputs/AppInput/AppInput";
import { Form, Formik } from "formik";
import AppButton from "@/components/buttons/AppButton/AppButton";
import * as Yup from "yup";
import { Box, Typography } from "@mui/material";
import BookingDetailsCard from "@/components/cards/BookingDetailsCard/BookingDetailsCard";
import BookingDetailsCardSkeleton from "@/components/BookingDetailsCardSkeleton/BookingDetailsCardSkeleton";
import { RetrieveBookingByEmail } from "@/utils/trip/retrieveBookingByEmail";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/types/RootState";
import { saveBookingCredentials } from "@/redux/reducer/bookingSlice";
import { usePreviousRoute } from "@/hooks/usePreviousRoute";

interface BookingFormProps {}
interface Booking {
  bookingRef: string;
  leadGuest: string;
  checkinDate: string;
  bookingDate: string;
  serviceInfo: string;
  agent: string;
  amount: number;
  currency: string;
  status: string;
}

const BookingForm: FC<BookingFormProps> = () => {
  const t = useTranslations();
  const [error, setError] = useState<null | string>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const dispatch = useDispatch();
  const bookingData = useSelector((state: RootState) => state.booking);
  const router = useRouter();
  const prevRoute = usePreviousRoute();

  // When trip_id disappears from URL (user pressed back), reset to form view
  useEffect(() => {
    if (router.isReady && !router.query.trip_id) {
      setBooking(null);
      setHasSubmitted(false);
      setError(null);
    }
  }, [router.isReady, router.query.trip_id]);

  const emailQuery =
    typeof router.query.email === "string" ? router.query.email : undefined;
  const bookingIdQuery =
    typeof router.query.trip_id === "string" ? router.query.trip_id : undefined;

  const shouldPopulate = prevRoute === "/confirm" || prevRoute === "/itinerary";

  const initialEmail = shouldPopulate
    ? (bookingData.email ?? "")
    : emailQuery ?? "";

  const initialBookingId = shouldPopulate
    ? (bookingData.bookingId ?? "")
    : bookingIdQuery ?? "";

  const validationSchema = Yup.object().shape({
    bookingId: Yup.string().required(t("bookingIdRequired")),
    email: Yup.string()
      .required(t("validation.email_required"))
      .test("is-valid-email", t("validation.email_invalid"), function (value) {
        if (!value) return true;
        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
      }),
  });

  const handleSubmit = async (values: { bookingId: string; email: string }) => {
    setHasSubmitted(true);
    setLoading(true);
    setError(null);
    try {
      dispatch(
        saveBookingCredentials({
          email: values.email,
          bookingId: values.bookingId,
        }),
      );

      const response = await RetrieveBookingByEmail(
        values.bookingId,
        values.email,
      );

      setBooking(response.response);

      if (!response.response || response.response.length === 0) {
        setError(t("noBookingMatchError"));
        return;
      }

      router.push(
        { pathname: "/manage-bookings", query: { trip_id: values.bookingId } },
        undefined,
        { shallow: true },
      );
    } catch {
      setError(t("noBookingMatchError"));
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center p-4 max-w-[500px] mx-auto py-6">
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          fontSize: { xs: "24px", sm: "26px" },
          lineHeight: { xs: "24px", sm: "32px" },
          color: "#1e2f56",
          textAlign: "center",
          marginBottom: "32px",
        }}
      >
        {t("RetrieveMyBookings")}
      </Typography>

      {!booking  && (
        <>
          <div className="text-base max-w-[450px] text-center">
            We just need your booking reference and email. You'll find them on
            top of your confirmation email.
          </div>
          <div className="w-full px-4 mt-6">
            <Formik
              initialValues={{ bookingId: initialBookingId, email: initialEmail }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, handleChange, handleBlur, touched, errors }) => (
                <Form className="w-full space-y-6">
                      {error && (
                    <div className="w-full border border-red-400 rounded-xl px-4 py-3 text-sm bg-red-50 text-center ">
                      {error}
                    </div>
                  )}
                  <Box>
                    <AppInput
                      name="email"
                      value={values.email}
                      onChange={handleChange("email")}
                      onBlur={handleBlur("email")}
                      onFocus={() => setError(null)}
                      autoComplete="off"
                      error={touched.email && errors.email}
                      labelClass="!font-medium !text-base leading-5 mb-2 !text-primaryText"
                      placeholder={t("emailPlaceholder")}
                      className="bg-gray-50 !h-[55px] !rounded-[12px] border !border-gray-300 hover:border-gray-300 focus-within:border-gray-300 !text-base !text-gray-600 px-3 outline-none"
                    />
                  </Box>

                  <AppInput
                    name="bookingId"
                    value={values.bookingId}
                    onChange={handleChange("bookingId")}
                    onBlur={handleBlur("bookingId")}
                    onFocus={() => setError(null)}
                    error={touched.bookingId && errors.bookingId}
                    autoComplete="off"
                    placeholder={t("bookingIdPlaceholder")}
                    labelClass="!font-medium !text-base leading-5 mb-2 !text-primaryText"
                    className="bg-gray-50 !h-[55px] !rounded-[12px] border !border-gray-300 hover:border-gray-300 focus-within:border-gray-300 !text-base !text-gray-600 px-3 outline-none"
                  />

                  <p className="text-xs text-[#66797f] text-center">
                    {t("bookingIdDescription")}
                  </p>

                  <AppButton
                    variant="primary"
                    label={t("continue")}
                    type="submit"
                    loading={loading}
                    textClassName="!text-base !leading-6"
                    className="h-[56px] w-full bg-primary !rounded-lg hover:bg-primaryHover text-white px-4"
                  />

                  <p className="text-xl text-center pt-5">{t("needToTalk")}</p>
                  <p className="text-base text-center">{t("bookingHelp")}</p>
                </Form>
              )}
            </Formik>
          </div>
        </>
      )}

      <div className="flex flex-col w-full items-start">
        {/* {loading && <BookingDetailsCardSkeleton />} */}
        {!loading && booking && <BookingDetailsCard bookingData={booking} />}
      
      </div>
    </div>
  );
};

export default BookingForm;